import { useMemo } from 'react';
import {
  Heading,
  Text,
  Icon,
  SimpleGrid,
  Center,
  Spinner,
  Image,
  VStack,
  Box,
  Spacer,
  Flex,
  chakra,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import PageContainer from '~/containers/PageContainer';
import ContentContainer from '~/containers/Content';
import Content from '~/containers/Content';

import SectionContainer from '~/containers/SectionContainer';
import BackwardBar from '~/components/BackwardBar';
import RestaurantItem from '~/components/RestaurantItem';
import EmptySection from '~/components/EmptySection';
import GoTopButton from '~/components/GoTopButton';
import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterLoginSection from '~/components/FooterLoginSection';

import authAtom from '~/recoil/atom/auth';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import displayStoresSelector from '~/recoil/selectors/displayStores';
import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import filterMealSearchFormAtom from '~/recoil/atom/filterMealSearchForm';
import { useRecoilValue } from 'recoil';

import useStoreList from '~/hooks/useStoreList';
import useSearchStore from '~/hooks/useSearchStore';
import useCartId from '~/hooks/useCartId';

import { FetchingDataStatus } from '~/types/utilities';
import { CartType } from '~/types/cart';

import fp from 'lodash/fp';
import getScheduleTimeString from '~/utils/getScheduleTimeString';
import parseISO from 'date-fns/parseISO';

import NoSearchResult from '~/assets/images/search.png';

import { IoRestaurantOutline } from 'react-icons/io5';

export default function SearchStore() {
  const navigate = useNavigate();
  const { groupId = '' } = useParams();

  const auth = useRecoilValue(authAtom);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const filterMealSearchForm = useRecoilValue(filterMealSearchFormAtom);
  const displayStores = useRecoilValue(displayStoresSelector);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const { cartId, cartType } = useCartId();

  const availableStores = useMemo(
    () => currentPickMealPoint.stores.filter((storeId) => displayStores.includes(storeId)),
    [displayStores, currentPickMealPoint.stores],
  );

  const searchStores = useMemo(() => {
    if (filterMealSearchForm.storeIds.length) {
      return filterMealSearchForm.storeIds;
    }
    if (filterMealSearchForm.searchText || filterMealSearchForm.tags.length > 0) {
      return availableStores;
    }
    return [];
  }, [
    filterMealSearchForm.searchText,
    filterMealSearchForm.storeIds,
    filterMealSearchForm.tags,
    availableStores,
  ]);

  const { storeList, fetchingStatus: fetchingStatusOfStore } = useStoreList(availableStores);
  const { storesFulfillSearch, fetchingStatus } = useSearchStore({
    ...filterMealSearchForm,
    storeIds: searchStores,
  });

  const stores = useMemo(
    () =>
      storeList.filter(
        ({ objectID, name }) =>
          storesFulfillSearch.includes(objectID) ||
          name.toLowerCase().includes(filterMealSearchForm.searchText.toLowerCase()),
      ),
    [storeList, storesFulfillSearch, filterMealSearchForm.searchText],
  );

  const searchText = filterMealSearchForm.searchText
    ? `「${filterMealSearchForm.searchText}」`
    : '';
  const searchStoresText = filterMealSearchForm.storeIds.length
    ? `「${filterMealSearchForm.storeIds
        .map((storeId) => storeList.find(({ objectID }) => objectID === storeId)?.name || '')
        .filter((name) => name)
        .join(',')}」`
    : '「全部餐廳」';
  const searchTagsText = filterMealSearchForm.tags.length
    ? `「${filterMealSearchForm.tags.join(',')}」`
    : '「全部素食分類」';

  const searchList = searchText
    ? [searchText, searchStoresText, searchTagsText]
    : [searchStoresText, searchTagsText];
  return (
    <PageContainer
      hideHeader
      hideNavBar
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => navigate(groupId ? `group/${groupId}` : '/')}
          />
          <Heading
            py={3}
            size="md"
            bgColor="white"
            textAlign="center"
            borderBottomWidth="1px"
            borderColor="primary"
          >
            餐廳搜尋結果
          </Heading>
        </>
      }
    >
      <Box bgColor="white" my={-4} py={4}>
        <ContentContainer flexGrow={1} minH="100vh" my={0} px={4}>
          <Text fontSize="sm" my={4}>
            搜尋
            <chakra.span fontSize="lg">{searchList.join('、')}</chakra.span>
            的結果
          </Text>
          <SectionContainer
            title="餐廳"
            icon={<Icon as={IoRestaurantOutline} color="secondary" boxSize="1.25rem" />}
          />
          <Text fontSize="sm" color="gray.500" mb={4}>
            符合搜尋結果: {stores.length} 個
          </Text>
          {fp.cond([
            [
              fp.where({
                state: fp.eq(FetchingDataStatus.LOADING),
              }),
              () => (
                <Center>
                  <Spinner size="lg" />
                </Center>
              ),
            ],
            [
              fp.where({
                state: fp.eq(FetchingDataStatus.FAIL),
              }),
              () => <EmptySection content="獲取資料發生錯誤請重試" />,
            ],
            [
              fp.where({
                state: fp.eq(FetchingDataStatus.SUCCESS),
                itemsLength: fp.eq(0),
              }),
              () => (
                <VStack spacing={6} py={5} alignItems="center">
                  <Text color="fontColor" fontSize="sm">
                    查無結果
                  </Text>
                  <Image src={NoSearchResult} w={180} />
                </VStack>
              ),
            ],
            [
              fp.where({
                state: fp.eq(FetchingDataStatus.SUCCESS),
                itemsLength: fp.lt(0),
              }),
              () => (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {stores.map((store) => (
                    <RestaurantItem
                      key={store.objectID}
                      name={store.name}
                      address={store.address}
                      image={store.image}
                      logo={store.logo}
                      time={getScheduleTimeString(
                        store.preOrderSetting.schedule,
                        store.officialHolidayISOString?.map((isoString) => parseISO(isoString)) ||
                          [],
                        pickMealAt,
                      )}
                      onNavigate={() =>
                        navigate(
                          groupId
                            ? `/group/${groupId}/store/${store.objectID}`
                            : `/store/${store.objectID}`,
                          { state: { previous: window.location.pathname } },
                        )
                      }
                    />
                  ))}
                </SimpleGrid>
              ),
            ],
            [fp.T, fp.always(null)],
          ])({
            state: fetchingStatus,
            itemsLength: stores.length,
          })}
        </ContentContainer>
        <Box position="sticky" bottom={0}>
          <Box px={4}>
            <Content py={2}>
              <Flex>
                <Spacer />
                <GoTopButton />
              </Flex>
            </Content>
          </Box>

          <>
            {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
              <FooterLoginSection />
            ) : (
              <FooterCartStatusBar
                onNavigate={() =>
                  navigate(fp.eq(cartType, CartType.CART_GROUP) ? `/group/${cartId}/cart` : '/cart')
                }
              />
            )}
          </>
        </Box>
      </Box>
    </PageContainer>
  );
}
